/**
 * 카드 별명 편집 모달
 * @CODE:API-302 | SPEC: olulo-mx-admin/.claude/specs/SPEC-302-payment-advanced-features.md
 */
import { useState, useEffect } from 'react';
import { X, CreditCard } from 'lucide-react';
import type { SavedCard } from '@/lib/api/types';

export interface CardNicknameModalProps {
  /** 편집할 카드 */
  card: SavedCard | null;
  /** 모달 표시 여부 */
  isOpen: boolean;
  /** 저장 중 여부 */
  isSaving?: boolean;
  /** 모달 닫기 */
  onClose: () => void;
  /** 저장 */
  onSave: (cardId: number, nickname: string | null) => Promise<void>;
  /** 번역 텍스트 */
  t: {
    title: string;
    subtitle: string;
    placeholder: string;
    cancel: string;
    save: string;
    saving: string;
    clearNickname: string;
    saveError?: string;
  };
}

export function CardNicknameModal({
  card,
  isOpen,
  isSaving = false,
  onClose,
  onSave,
  t,
}: CardNicknameModalProps) {
  const [nickname, setNickname] = useState('');
  const [error, setError] = useState<string | null>(null);

  // 카드가 변경되면 별명 초기화
  useEffect(() => {
    if (card) {
      setNickname(card.nickname || '');
      setError(null);
    }
  }, [card]);

  if (!isOpen || !card) return null;

  const handleSave = async () => {
    setError(null);
    const trimmedNickname = nickname.trim();
    try {
      await onSave(card.id, trimmedNickname || null);
      onClose();
    } catch (err) {
      // 저장 실패 시 에러 표시, 모달 유지
      setError(err instanceof Error ? err.message : (t.saveError || 'Failed to save'));
    }
  };

  const handleClear = () => {
    setNickname('');
  };

  // 모달 닫기 시 에러 상태 초기화
  const handleClose = () => {
    setError(null);
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* 배경 오버레이 */}
      <div
        className="absolute inset-0 bg-black/50"
        onClick={handleClose}
      />

      {/* 모달 컨텐츠 */}
      <div className="relative bg-background rounded-2xl w-full max-w-sm mx-4 p-6 shadow-xl">
        {/* 닫기 버튼 */}
        <button
          onClick={handleClose}
          className="absolute top-4 right-4 p-1 text-muted-foreground hover:text-foreground"
          type="button"
        >
          <X className="w-5 h-5" />
        </button>

        {/* 헤더 */}
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 bg-secondary/10 rounded-full flex items-center justify-center">
            <CreditCard className="w-5 h-5 text-secondary" />
          </div>
          <div>
            <h3 className="font-semibold text-foreground">{t.title}</h3>
            <p className="text-sm text-muted-foreground">{t.subtitle}</p>
          </div>
        </div>

        {/* 카드 정보 */}
        <div className="mb-4 p-3 bg-muted rounded-lg">
          <p className="text-sm font-medium text-foreground">
            {card.masked_display}
          </p>
          <p className="text-xs text-muted-foreground">
            {card.expiry_display}
            {card.card_holder && ` · ${card.card_holder}`}
          </p>
        </div>

        {/* 별명 입력 */}
        <div className="mb-6">
          <label htmlFor="card-nickname-input" className="sr-only">
            {t.title}
          </label>
          <input
            id="card-nickname-input"
            type="text"
            value={nickname}
            onChange={(e) => setNickname(e.target.value)}
            placeholder={t.placeholder}
            className="w-full px-4 py-3 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
            maxLength={50}
            aria-describedby="nickname-hint"
          />
          <p id="nickname-hint" className="sr-only">
            최대 50자까지 입력 가능합니다
          </p>
          {nickname && (
            <button
              onClick={handleClear}
              className="mt-2 text-sm text-muted-foreground hover:text-foreground"
              type="button"
              aria-label={t.clearNickname}
            >
              {t.clearNickname}
            </button>
          )}
        </div>

        {/* 에러 메시지 */}
        {error && (
          <div className="mb-4 p-3 bg-destructive/10 border border-destructive/20 rounded-lg">
            <p className="text-sm text-destructive">{error}</p>
          </div>
        )}

        {/* 버튼 */}
        <div className="flex gap-3">
          <button
            onClick={handleClose}
            className="flex-1 px-4 py-3 border border-border rounded-lg text-foreground font-medium hover:bg-muted"
            type="button"
            disabled={isSaving}
          >
            {t.cancel}
          </button>
          <button
            onClick={handleSave}
            className="flex-1 px-4 py-3 bg-primary text-white rounded-lg font-medium hover:bg-primary-hover disabled:opacity-50 disabled:cursor-not-allowed"
            type="button"
            disabled={isSaving}
          >
            {isSaving ? t.saving : t.save}
          </button>
        </div>
      </div>
    </div>
  );
}

export default CardNicknameModal;
